import { useState } from 'react';
import { Menu, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const Sidebar = ({ 
  menuItems, 
  activeTab, 
  setActiveTab, 
  header, 
  footer,
  brandColor = 'blue'
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const toggleSidebar = () => setIsOpen(!isOpen);
  const toggleMobileSidebar = () => setIsMobileOpen(!isMobileOpen);

  return (
    <>
      {/* Mobile Hamburger Button */}
      <button
        onClick={toggleMobileSidebar}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-gray-900 text-white rounded-lg shadow-lg hover:bg-gray-800 transition-colors"
      >
        {isMobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Overlay for mobile */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={toggleMobileSidebar}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed top-0 left-0 h-full bg-gray-900 text-white shadow-2xl z-40 transition-all duration-300 ease-in-out
          flex flex-col
          ${isOpen ? 'w-64' : 'w-20'}
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Header */}
        <div className={`p-6 border-b border-gray-800 flex-shrink-0 ${!isOpen && 'px-3'}`}>
          {header && header(isOpen)}
        </div>

        {/* Toggle Button - Desktop Only */}
        <button
          onClick={toggleSidebar}
          className={`
            hidden lg:flex absolute -right-3 top-24 w-6 h-6 bg-gray-900 border border-gray-700 rounded-full
            items-center justify-center hover:bg-gray-800 transition-colors z-50
          `}
        >
          {isOpen ? (
            <ChevronLeft className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>

        {/* Menu Items */}
        <nav className="flex-1 overflow-y-auto py-6 px-3 min-h-0 custom-scrollbar">
          <div className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id);
                    setIsMobileOpen(false);
                  }}
                  className={`
                    w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200
                    ${isActive 
                      ? `bg-${brandColor}-600 text-white shadow-lg` 
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }
                    ${!isOpen && 'justify-center'}
                    group relative
                  `}
                  data-testid={`sidebar-${item.id}`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-gray-400 group-hover:text-white'}`} />
                  
                  {isOpen ? (
                    <span className="font-medium text-sm">{item.label}</span>
                  ) : (
                    <div className="absolute left-full ml-6 px-2 py-1 bg-gray-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
                      {item.label}
                    </div>
                  )}
                  
                  {item.badge && isOpen && (
                    <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Footer */}
        {footer && (
          <div className={`p-4 border-t border-gray-800 flex-shrink-0 ${!isOpen && 'px-3'}`}>
            {footer(isOpen)}
          </div>
        )}
      </div>

      {/* Spacer for main content */}
      <div className={`hidden lg:block transition-all duration-300 ${isOpen ? 'w-64' : 'w-20'}`} />
    </>
  );
};

export default Sidebar;
